import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Lock,
  User,
  Eye,
  EyeOff,
  ShieldCheck,
  ArrowRight,
  Sparkles,
  CheckCircle2,
  AlertCircle
} from 'lucide-react';

import { useAppDispatch } from '@app/hooks';
import { setCredentials } from '@features/auth/store/authSlice';
import { ROUTES } from '@constants/routes';
import { STORAGE_KEYS } from '@constants/storage';
import { authClient } from '@services/axiosClient';
import { toast } from '@components/ui';

export const LoginPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const [userName, setUserName] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(true);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successInfo, setSuccessInfo] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccessInfo(null);

    if (!userName.trim() || !password.trim()) {
      setError('Vui lòng nhập đầy đủ Tên đăng nhập và Mật khẩu.');
      return;
    }

    setIsLoading(true);

    try {
      // Gọi trực tiếp API Login từ Backend PNR: /api/v1/auth/login
      const response = await authClient.login({
        userName: userName.trim(),
        password: password.trim(),
        rememberMe,
      });

      const accessToken = response.accessToken;
      const refreshToken = response.refreshToken;
      const userObj = response.user;

      if (accessToken) {
        // Lưu access token và refresh token vào localStorage
        localStorage.setItem(STORAGE_KEYS.AUTH_TOKEN, accessToken);
        if (refreshToken) {
          localStorage.setItem(STORAGE_KEYS.REFRESH_TOKEN, refreshToken);
        }
        if (userObj) {
          localStorage.setItem(STORAGE_KEYS.AUTH_USER, JSON.stringify(userObj));
        }

        // Cập nhật Redux store
        dispatch(
          setCredentials({
            user: {
              id: userObj?.id?.toString() || 'usr_01',
              name: userObj?.displayName || userObj?.userName || userName,
              email: `${userObj?.userName || userName}@ngsxnc.vn`,
            },
            token: accessToken,
          })
        );

        setSuccessInfo('Đăng nhập thành công! Đang chuyển hướng...');
        setTimeout(() => {
          navigate(ROUTES.ADMIN);
        }, 400);
      } else {
        throw new Error('Không nhận được access token từ máy chủ.');
      }
    } catch (apiErr: any) {
      console.warn('Backend API login error, checking fallback demo:', apiErr);
      // Trích xuất thông điệp lỗi chi tiết từ Backend
      const detailMsg =
        apiErr?.response?.data?.detail ||
        apiErr?.response?.data?.title ||
        apiErr?.response?.data?.message ||
        apiErr?.message ||
        'Tài khoản hoặc mật khẩu không chính xác trên hệ thống backend.';

      setError(`Lỗi đăng nhập: ${detailMsg}`);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#070b14] px-4 py-12 text-slate-100">
      {/* Background glowing orbs */}
      <div className="pointer-events-none absolute -top-40 left-1/2 h-[500px] w-[500px] -translate-x-1/2 rounded-full bg-blue-600/15 blur-[120px]" />
      <div className="pointer-events-none absolute -bottom-40 right-10 h-[400px] w-[400px] rounded-full bg-indigo-600/10 blur-[120px]" />
      <div className="pointer-events-none absolute top-1/3 -left-20 h-[350px] w-[350px] rounded-full bg-cyan-600/10 blur-[100px]" />

      {/* Grid pattern overlay */}
      <div
        className="pointer-events-none absolute inset-0 opacity-[0.03]"
        style={{
          backgroundImage: 'radial-gradient(circle at 1px 1px, #ffffff 1px, transparent 0)',
          backgroundSize: '32px 32px',
        }}
      />

      <div className="relative z-10 w-full max-w-md">
        {/* Top brand header */}
        <div className="mb-8 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-blue-500/30 bg-blue-500/10 px-3.5 py-1.5 text-xs font-semibold text-blue-400 shadow-inner">
            <Sparkles className="h-3.5 w-3.5 animate-pulse text-blue-400" />
            <span>PNR Client Hub • Trang Quản Trị</span>
          </div>

          <h1 className="mt-4 text-3xl font-extrabold tracking-tight text-white sm:text-4xl">
            Đăng nhập hệ thống
          </h1>
          <p className="mt-2 text-sm text-slate-400">
            Kết nối trực tiếp với backend <span className="font-semibold text-blue-400">pnr-client.ngsxnc.vn</span>
          </p>
        </div>

        {/* Login Card */}
        <div className="rounded-2xl border border-slate-800 bg-[#0f172a]/80 p-8 shadow-2xl backdrop-blur-xl transition-all">
          {error && (
            <div className="mb-5 flex items-start gap-2.5 rounded-xl border border-red-500/30 bg-red-500/10 p-3.5 text-sm text-red-400">
              <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
              <span>{error}</span>
            </div>
          )}

          {successInfo && (
            <div className="mb-5 flex items-center gap-2.5 rounded-xl border border-emerald-500/30 bg-emerald-500/10 p-3.5 text-sm text-emerald-400">
              <CheckCircle2 className="h-4 w-4 shrink-0" />
              <span>{successInfo}</span>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-5">
            {/* Username Field */}
            <div>
              <label className="mb-2 block text-xs font-semibold uppercase tracking-wider text-slate-300">
                Tên đăng nhập (Username)
              </label>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                  <User className="h-4 w-4" />
                </div>
                <input
                  type="text"
                  value={userName}
                  onChange={(e) => setUserName(e.target.value)}
                  placeholder="Nhập tên đăng nhập..."
                  required
                  autoFocus
                  className="w-full rounded-xl border border-slate-700/80 bg-[#131d33] py-2.5 pr-4 pl-10 text-sm text-white placeholder-slate-500 transition-all focus:border-blue-500 focus:bg-[#16223d] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <div className="mb-2 flex items-center justify-between">
                <label className="block text-xs font-semibold uppercase tracking-wider text-slate-300">
                  Mật khẩu
                </label>
                <a
                  href="#forgot"
                  onClick={(e) => {
                    e.preventDefault();
                    toast.info('Chức năng cấp lại mật khẩu: Vui lòng liên hệ Quản trị viên PNR.');
                  }}
                  className="text-xs text-blue-400 hover:text-blue-300 hover:underline transition-colors"
                >
                  Quên mật khẩu?
                </a>
              </div>
              <div className="relative">
                <div className="pointer-events-none absolute inset-y-0 left-0 flex items-center pl-3.5 text-slate-400">
                  <Lock className="h-4 w-4" />
                </div>
                <input
                  type={showPassword ? 'text' : 'password'}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  className="w-full rounded-xl border border-slate-700/80 bg-[#131d33] py-2.5 pr-11 pl-10 text-sm text-white placeholder-slate-500 transition-all focus:border-blue-500 focus:bg-[#16223d] focus:outline-none focus:ring-2 focus:ring-blue-500/20"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute inset-y-0 right-0 flex items-center pr-3.5 text-slate-400 hover:text-slate-200 transition-colors cursor-pointer"
                >
                  {showPassword ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                </button>
              </div>
            </div>

            {/* Remember Me */}
            <div className="flex items-center justify-between pt-1">
              <label className="flex cursor-pointer items-center gap-2 select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  className="h-4 w-4 rounded border-slate-700 bg-[#131d33] text-blue-600 focus:ring-blue-500/40 focus:ring-offset-0"
                />
                <span className="text-xs text-slate-300">Ghi nhớ đăng nhập</span>
              </label>
              <div className="flex items-center gap-1 text-[11px] text-slate-400">
                <ShieldCheck className="h-3.5 w-3.5 text-emerald-400" />
                <span>JWT Bearer</span>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={isLoading}
              className="group relative flex w-full items-center justify-center gap-2 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 py-3 text-sm font-semibold text-white shadow-lg shadow-blue-500/25 transition-all hover:from-blue-500 hover:to-indigo-500 hover:shadow-blue-500/35 active:scale-[0.99] disabled:opacity-60 cursor-pointer"
            >
              {isLoading ? (
                <div className="flex items-center gap-2">
                  <div className="h-4 w-4 animate-spin rounded-full border-2 border-white border-t-transparent" />
                  <span>Đang xác thực với PNR API...</span>
                </div>
              ) : (
                <>
                  <span>Đăng nhập hệ thống</span>
                  <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </>
              )}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};
